import { useEffect, useState } from "react";
import type { QuestionType } from "@/types/question";
import { getQuestionTypeIcon } from "@/utils/questionIcons";

interface SelectedQuestion {
	id: number;
	label: string;
	type: QuestionType;
	required?: boolean;
}

interface Section {
	id: string;
	title: string;
	questions: SelectedQuestion[];
}

interface SelectedQuestionsListProps {
	title?: string;
	sections: Section[];
	onMoveQuestion?: (
		questionId: number,
		fromSectionId: string,
		toSectionId: string,
		targetIndex?: number
	) => void;
	onReorder?: (questionId: number, direction: "up" | "down") => void;
	onQuestionClick?: (question: SelectedQuestion) => void;
	allowReorder?: boolean;
}

interface DragState {
	questionId: number;
	sectionId: string;
	startY: number;
	startX: number;
	currentX: number;
	currentY: number;
}

export const SelectedQuestionsList = ({
	title = "PERGUNTAS DO FORMULÁRIO",
	sections,
	onMoveQuestion,
	onReorder,
	onQuestionClick,
	allowReorder = false,
}: SelectedQuestionsListProps) => {
	const [dragState, setDragState] = useState<DragState | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragOverQuestionId, setDragOverQuestionId] = useState<number | null>(
		null
	);
	const [dragOverPosition, setDragOverPosition] = useState<
		"before" | "after" | null
	>(null);
	const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(
		null
	);
	const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(
		null
	);

	const canReorder = allowReorder && typeof onMoveQuestion === "function";

	const handleMouseDown = (
		e: React.MouseEvent,
		questionId: number,
		sectionId: string
	) => {
		if (!canReorder || e.button !== 0) return;
		setDragState({
			questionId,
			sectionId,
			startY: e.clientY,
			startX: e.clientX,
			currentX: e.clientX,
			currentY: e.clientY,
		});
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!dragState) return;
		setDragState({ ...dragState, currentX: e.clientX, currentY: e.clientY });
		const deltaY = Math.abs(e.clientY - dragState.startY);
		if (deltaY > 5 && !isDragging) setIsDragging(true);
	};

	const handleMouseUp = () => {
		setDragState(null);
		setIsDragging(false);
		setDragOverQuestionId(null);
		setDragOverPosition(null);
		setDragOverSectionId(null);
	};

	const handleQuestionMouseEnter = (
		e: React.MouseEvent,
		questionId: number,
		sectionId: string,
		index: number
	) => {
		if (
			!(isDragging && dragState && canReorder) ||
			dragState.questionId === questionId
		)
			return;

		const rect = e.currentTarget.getBoundingClientRect();
		const midpoint = rect.top + rect.height / 2;
		const position = e.clientY < midpoint ? "before" : "after";

		setDragOverQuestionId(questionId);
		setDragOverPosition(position);
		setDragOverSectionId(sectionId);
	};

	const handleQuestionMouseUp = (
		targetQuestionId: number,
		targetSectionId: string,
		targetIndex: number
	) => {
		if (!(isDragging && dragState && canReorder && onMoveQuestion))
			return handleMouseUp();
		if (dragState.questionId === targetQuestionId) return handleMouseUp();

		let finalIndex = targetIndex;
		if (dragOverPosition === "after") finalIndex += 1;

		if (dragState.sectionId === targetSectionId) {
			const section = sections.find((s) => s.id === targetSectionId);
			if (section) {
				const draggedIndex = section.questions.findIndex(
					(q) => q.id === dragState.questionId
				);
				if (draggedIndex !== -1 && draggedIndex < finalIndex) finalIndex -= 1;
			}
		}

		onMoveQuestion(
			dragState.questionId,
			dragState.sectionId,
			targetSectionId,
			finalIndex
		);
		handleMouseUp();
	};

	const handleEmptySectionMouseEnter = (sectionId: string) => {
		if (!(isDragging && dragState && canReorder)) return;
		setDragOverSectionId(sectionId);
		setDragOverQuestionId(null);
		setDragOverPosition(null);
	};

	const handleEmptySectionMouseUp = (targetSectionId: string) => {
		if (!(isDragging && dragState && canReorder && onMoveQuestion))
			return handleMouseUp();
		onMoveQuestion(
			dragState.questionId,
			dragState.sectionId,
			targetSectionId,
			0
		);
		handleMouseUp();
	};

	useEffect(() => {
		window.addEventListener("mouseup", handleMouseUp);
		return () => window.removeEventListener("mouseup", handleMouseUp);
	}, []);

	const draggedQuestion =
		isDragging && dragState
			? sections
					.find((s) => s.id === dragState.sectionId)
					?.questions.find((q) => q.id === dragState.questionId)
			: null;
	const DraggedQuestionIcon = draggedQuestion
		? getQuestionTypeIcon(draggedQuestion.type)
		: null;

	return (
		<>
			<div
				className="flex h-screen flex-col gap-3 overflow-y-auto rounded-2xl border border-[rgba(0,0,0,0.08)] bg-white p-6 shadow-md"
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
			>
				<h2 className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
					{title}
				</h2>

				<div className="mt-4 flex flex-col gap-6">
					{sections.length === 0 ? (
						<div className="py-8 text-center text-gray-400 text-sm">
							Nenhuma pergunta
						</div>
					) : (
						sections.map((section) => (
							<div className="space-y-3" key={section.id}>
								{section.title && (
									<h3 className="mb-3 break-all px-2 font-semibold text-gray-700 text-sm">
										{section.title}
									</h3>
								)}

								{section.questions.length === 0 ? (
									<div
										className="relative h-12 w-full transition-colors"
										onMouseEnter={() =>
											canReorder && handleEmptySectionMouseEnter(section.id)
										}
										onMouseUp={() =>
											canReorder && handleEmptySectionMouseUp(section.id)
										}
									>
										{canReorder && dragOverSectionId === section.id && (
											<div className="-translate-y-1/2 absolute top-1/2 left-0 h-1 w-full rounded bg-blue-500" />
										)}
									</div>
								) : (
									<ul className="space-y-2">
										{section.questions.map((question, index) => {
											const Icon = getQuestionTypeIcon(question.type);
											const isBeingDragged =
												isDragging && dragState?.questionId === question.id;

											return (
												<li className="relative" key={question.id}>
													{dragOverQuestionId === question.id &&
														dragOverPosition === "before" && (
															<div className="mb-1 h-0.5 w-full rounded bg-blue-500 transition-all" />
														)}

													<button
														className={`flex w-full items-center gap-3 rounded-lg p-3 transition-all ${selectedQuestionId === question.id ? "border border-blue-500 bg-blue-100 text-blue-700" : ""}
                              ${
																canReorder
																	? isBeingDragged
																		? "scale-95 border-blue-200 bg-blue-50 opacity-20"
																		: "cursor-grab active:cursor-grabbing"
																	: "cursor-pointer border-gray-200 bg-gray-50"
															}`}
														onClick={() => {
															setSelectedQuestionId(question.id);
															onQuestionClick?.(question);
														}}
														onMouseDown={(e) =>
															canReorder &&
															handleMouseDown(e, question.id, section.id)
														}
														onMouseEnter={(e) =>
															handleQuestionMouseEnter(
																e,
																question.id,
																section.id,
																index
															)
														}
														onMouseUp={() =>
															handleQuestionMouseUp(
																question.id,
																section.id,
																index
															)
														}
														type="button"
													>
														<div
															className={`flex items-center justify-center rounded-md p-2 ${
																selectedQuestionId === question.id
																	? "bg-blue-100 text-blue-600"
																	: "bg-gray-100 text-gray-600"
															}`}
														>
															<Icon size={20} />
														</div>
														<span className="flex-1 text-left font-medium text-gray-700 text-sm">
															{question.label}
														</span>
													</button>

													{dragOverQuestionId === question.id &&
														dragOverPosition === "after" && (
															<div className="mt-1 h-0.5 w-full rounded bg-blue-500 transition-all" />
														)}
												</li>
											);
										})}
									</ul>
								)}
							</div>
						))
					)}
				</div>
			</div>

			{canReorder &&
				isDragging &&
				dragState &&
				draggedQuestion &&
				DraggedQuestionIcon && (
					<div
						className="pointer-events-none fixed z-50"
						style={{
							left: dragState.currentX,
							top: dragState.currentY,
						}}
					>
						<div className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-2xl">
							<div className="flex items-center justify-center rounded-md bg-blue-100 p-2 text-blue-600">
								<DraggedQuestionIcon size={20} />
							</div>
							<span className="max-w-xs truncate font-medium text-gray-700 text-sm">
								{draggedQuestion.label}
							</span>
						</div>
					</div>
				)}
		</>
	);
};
